import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Plus, 
  Search, 
  Handshake, 
  Loader2, 
  MoreHorizontal,
  Pencil,
  Trash2,
  Globe,
  Mail,
  Phone,
  Building2,
  Users,
  Eye,
  UserPlus
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { usePartners, useCreatePartner, useUpdatePartner, useDeletePartner, useStudents, useAssignCandidate } from '../lib/hooks';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../lib/translations';

const initialFormData = {
  name: '',
  email: '',
  password: '',
  organization_name: '',
  type: 'school',
  country: 'Allemagne',
  city: '',
  contact_phone: '',
  website: '',
  notes: '',
  status: 'active',
};

const PartnersPage = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [selectedStudentId, setSelectedStudentId] = useState('');

  const { data: partnersData, isLoading, refetch } = usePartners({ search: searchTerm, type: typeFilter !== 'all' ? typeFilter : undefined, status: statusFilter !== 'all' ? statusFilter : undefined });
  const { data: studentsData } = useStudents({ per_page: 100 });
  const createPartner = useCreatePartner();
  const updatePartner = useUpdatePartner(selectedPartner?.id);
  const deletePartner = useDeletePartner();
  const assignCandidate = useAssignCandidate(selectedPartner?.id);

  const partners = partnersData?.partners || [];
  const students = studentsData?.students || [];

  // Filter students who are not already assigned to the selected partner
  const availableStudents = students.filter(s => !s.partner_id || s.partner_id !== selectedPartner?.id);

  const handleOpenDialog = (partner = null) => {
    if (partner) {
      setSelectedPartner(partner);
      // Note: We can't edit existing partners with the full form since we don't have all fields
      setFormData({
        name: partner.name || '',
        email: partner.email || '',
        password: '',
        organization_name: partner.organization_name || '',
        type: partner.type || 'school',
        country: partner.country || 'Allemagne',
        city: partner.city || '',
        contact_phone: partner.contact_phone || '',
        website: partner.website || '',
        notes: partner.notes || '',
        status: partner.status || 'active',
      });
    } else {
      setSelectedPartner(null);
      setFormData(initialFormData);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (selectedPartner) {
        await updatePartner.mutateAsync({
          organization_name: formData.organization_name,
          country: formData.country,
          city: formData.city,
          type: formData.type,
          contact_phone: formData.contact_phone || null,
          website: formData.website || null,
          notes: formData.notes || null,
          status: formData.status || selectedPartner.status,
        });
        toast.success('Partenaire modifié avec succès');
      } else {
        await createPartner.mutateAsync(formData);
        toast.success('Partenaire créé avec succès');
      }
      setDialogOpen(false);
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'opération");
    }
  };

  const handleDelete = async () => {
    if (!selectedPartner) return;
    
    try {
      await deletePartner.mutateAsync(selectedPartner.id);
      toast.success('Partenaire supprimé avec succès');
      setDeleteDialogOpen(false);
      refetch();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleOpenAssignDialog = (partner) => {
    setSelectedPartner(partner);
    setSelectedStudentId('');
    setAssignDialogOpen(true);
  };

  const handleAssignStudent = async () => {
    if (!selectedStudentId || !selectedPartner) return;
    
    try {
      await assignCandidate.mutateAsync(selectedStudentId);
      toast.success('Candidat assigné avec succès');
      setAssignDialogOpen(false);
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'assignation");
    }
  };

  const typeLabels = {
    school: 'École',
    company: 'Entreprise',
    agency: 'Agence',
  };

  const typeColors = {
    school: 'bg-secondary/10 text-secondary',
    company: 'bg-primary/10 text-primary',
    agency: 'bg-accent/10 text-accent',
  };

  const statusColors = {
    active: 'bg-success/10 text-success',
    inactive: 'bg-muted text-muted-foreground',
  };

  const statusLabels = {
    active: 'Actif',
    inactive: 'Inactif',
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="partners-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">{t('partners_title', lang)}</h1>
          <p className="text-muted-foreground mt-1">Gérez les partenaires et leurs candidats</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="btn-active" data-testid="add-partner-btn">
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un partenaire
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t("partners_search", lang)}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="partner-search-input"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full md:w-40" data-testid="partner-type-filter">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all_types', lang)}</SelectItem>
                <SelectItem value="school">École</SelectItem>
                <SelectItem value="company">Entreprise</SelectItem>
                <SelectItem value="agency">Agence</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-40" data-testid="partner-status-filter">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                <SelectItem value="active">{t('status_active', lang)}</SelectItem>
                <SelectItem value="inactive">{t('status_inactive', lang)}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : partners.length === 0 ? (
            <div className="text-center py-12">
              <Handshake className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-semibold text-lg mb-1">Aucun partenaire trouvé</h3>
              <p className="text-muted-foreground">Ajoutez votre premier partenaire pour commencer</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('partners_organization', lang)}</TableHead>
                  <TableHead>{t('partners_type', lang)}</TableHead>
                  <TableHead className="hidden md:table-cell">{t('partners_location', lang)}</TableHead>
                  <TableHead>{t('partners_candidates', lang)}</TableHead>
                  <TableHead>{t('students_status', lang)}</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partners.map((partner) => (
                  <TableRow key={partner.id} className="hover:bg-muted/50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-secondary" />
                        </div>
                        <div>
                          <p className="font-medium">{partner.organization_name}</p>
                          <p className="text-sm text-muted-foreground">{partner.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={typeColors[partner.type]}>
                        {typeLabels[partner.type] || partner.type}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className="text-muted-foreground">
                        {partner.city ? `${partner.city}, ` : ''}{partner.country}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium">{partner.assigned_students_count || 0}</span>
                        {partner.accepted_count > 0 && (
                          <Badge variant="secondary" className="bg-success/10 text-success text-xs">
                            {partner.accepted_count} acceptés
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={statusColors[partner.status]}>
                        {statusLabels[partner.status] || partner.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`partner-actions-${partner.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleOpenAssignDialog(partner)}>
                            <UserPlus className="w-4 h-4 mr-2" />
                            Assigner un candidat
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleOpenDialog(partner)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Modifier
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => {
                              setSelectedPartner(partner);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Partner Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedPartner ? 'Modifier le partenaire' : 'Ajouter un partenaire'}
            </DialogTitle>
            <DialogDescription>
              {selectedPartner ? 'Modifiez les informations du partenaire' : 'Créez un nouveau partenaire avec un compte utilisateur'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom du contact *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="partner-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="partner-email-input"
                />
              </div>
            </div>

            {!selectedPartner && (
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe *</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="partner-password-input"
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="organization_name">Nom de l'organisation *</Label>
                <Input
                  id="organization_name"
                  value={formData.organization_name}
                  onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                  required
                  data-testid="partner-org-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">{t('partners_type', lang)}</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger data-testid="partner-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="school">École / Université</SelectItem>
                    <SelectItem value="company">Entreprise</SelectItem>
                    <SelectItem value="agency">Agence</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="country">Pays *</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  required
                  data-testid="partner-country-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">Ville *</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                  data-testid="partner-city-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_phone">{t('students_phone', lang)}</Label>
                <Input
                  id="contact_phone"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  data-testid="partner-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">{lang === 'de' ? 'Website' : 'Site web'}</Label>
                <Input
                  id="website"
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://example.com"
                  data-testid="partner-website-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">{t('course_detail_grades', lang)}</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                data-testid="partner-notes-input"
              />
            </div>

            {selectedPartner && (
              <div className="space-y-2">
                <Label>{t('students_status', lang)}</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="partner-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t('status_active', lang)}</SelectItem>
                    <SelectItem value="inactive">{t('status_inactive', lang)}</SelectItem>
                    <SelectItem value="prospect">{lang === 'de' ? 'Interessent' : 'Prospect'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={createPartner.isPending || updatePartner.isPending} data-testid="partner-submit-btn">
                {(createPartner.isPending || updatePartner.isPending) ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  selectedPartner ? 'Enregistrer' : 'Créer le partenaire'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer le partenaire "{selectedPartner?.organization_name}"? 
              Cette action est irréversible et supprimera également le compte utilisateur associé.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete} 
              disabled={deletePartner.isPending}
              data-testid="confirm-delete-partner-btn"
            >
              {deletePartner.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Candidate Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">{t('partners_assign', lang)}</DialogTitle>
            <DialogDescription>
              Assignez un étudiant au partenaire "{selectedPartner?.organization_name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Sélectionner un étudiant</Label>
              <Select value={selectedStudentId} onValueChange={setSelectedStudentId}>
                <SelectTrigger data-testid="student-select">
                  <SelectValue placeholder="Choisir un étudiant..." />
                </SelectTrigger>
                <SelectContent>
                  {availableStudents.map((student) => (
                    <SelectItem key={student.id} value={student.id}>
                      {student.first_name} {student.last_name} - {student.german_level}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {availableStudents.length === 0 && (
              <p className="text-sm text-muted-foreground">
                Aucun étudiant disponible pour l'assignation.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleAssignStudent} 
              disabled={!selectedStudentId || assignCandidate.isPending}
            >
              {assignCandidate.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Assigner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnersPage;
